import { BubbleButton, BubbleFormGroup } from './BubbleUI';
import BubbleTable, { BubbleTableFields } from './BubbleUI/BubbleTable';
import React, { useState } from 'react';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { ReplacedDict, TranslatedReplacedDict } from '@go-shared/types/mergedVars';
import { Mcm } from '@go-shared/types/mcm';
import McmVisibilityTable from './McmVisibilityTable';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { getHourFormat } from '@go-shared/lib/property';
import { Content } from '@go-shared/types/content';
import { getBasicTimeString } from '@go-shared/lib/event';
import AudienceVisibilityTable from './AudienceVisibilityTable';
import { Audience } from '@go-shared/types/audience';
import { DefaultedText } from './DefaultedText';
import { stripHTML, truncate } from '@go-shared/lib/string';
import { isNotBasicContentTemplate } from '@go-shared/lib/contentTemplate';

interface MergedVariableItem {
  key: string;
  label: string;
  language: string;
  value: string;
}

interface ContentsGlanceProps {
  content: Content;
  locked?: boolean;
  name?: string;
  timeString?: string;
  description?: string;
  onUpdateContent?: (content: Content) => void;
}

export default function ContentsGlance(props: ContentsGlanceProps) {
  const { defaultLanguage } = useServerInfo();
  const { activeProperty: property } = useActiveProperty();
  const { content } = props;
  const { template, mergedVars } = content;
  const [showAllDesc, setShowAllDesc] = useState(false);

  const hourFormat = getHourFormat(property);

  const mvItems: MergedVariableItem[] = [];
  const replacements: ReplacedDict = mergedVars?.replacements || {};
  const translations: TranslatedReplacedDict = mergedVars?.translations || {};

  Object.keys(replacements).forEach(k => {
    if (replacements[k].enabled) {
      mvItems.push({
        key: `${defaultLanguage.key}-${k}`,
        label: k,
        language: defaultLanguage.key,
        value: replacements[k].value,
      });
    }
  });

  Object.keys(translations).forEach(l => {
    Object.keys(translations[l]).forEach(k => {
      if (translations[l][k].enabled) {
        mvItems.push({
          key: `${l}-${k}`,
          label: k,
          language: l,
          value: translations[l][k].value,
        });
      }
    });
  });

  const mvFields: Array<BubbleTableFields<MergedVariableItem>> = [
    { key: 'label' },
    { key: 'language' },
    { key: 'value' },
  ];

  async function onUpdateContentMcm(mcm: Mcm) {
    if (props.onUpdateContent) {
      await props.onUpdateContent({ ...content, mcm });
    }
  }

  async function onUpdateContentAudience(audience: Audience) {
    if (props.onUpdateContent) {
      await props.onUpdateContent({ ...content, audience });
    }
  }

  function truncateAndStripHTML(description: string): string {
    const plainText = stripHTML(description);
    return truncate(plainText, 100);
  }

  return (
    <div className="grid grid-cols-6 gap-4">
      <BubbleFormGroup label="Name" className="col-span-3">
        {props.name || template.name}
      </BubbleFormGroup>
      <BubbleFormGroup label="Time" className="col-span-3">
        {props.timeString || getBasicTimeString(content, hourFormat)}
      </BubbleFormGroup>
      {isNotBasicContentTemplate(template) && (
        <BubbleFormGroup label="Description" className="col-span-6">
          <DefaultedText
            html={showAllDesc}
            val={
              showAllDesc
                ? props.description || template.description
                : truncateAndStripHTML(props.description || template.description)
            }
          />
          {!showAllDesc && (
            <BubbleButton
              variant="ocean"
              inverse
              className="p-0-i ml-1"
              onClick={() => setShowAllDesc(true)}
            >
              Show All
            </BubbleButton>
          )}
        </BubbleFormGroup>
      )}
      <BubbleFormGroup className="col-span-6" label="Endpoints">
        <McmVisibilityTable
          isTemplate={false}
          readOnly={props.locked}
          sourceMcm={content.mcm}
          parentMcm={content.template.mcm}
          onUpdateMcm={onUpdateContentMcm}
        />
      </BubbleFormGroup>
      <BubbleFormGroup className="col-span-6" label="Audience">
        <AudienceVisibilityTable
          isTemplate={false}
          readOnly={props.locked}
          sourceAud={content.audience}
          parentAud={content.template.audience}
          onUpdateAudience={onUpdateContentAudience}
        />
      </BubbleFormGroup>
      <BubbleFormGroup className="col-span-6" label="Merged Variables">
        <BubbleTable
          slim
          responsive
          hideHeader
          striped
          bordered
          headVariant="navy"
          keyExtractor={e => e.key}
          stickyHeader={0}
          items={mvItems}
          fields={mvFields}
        />
      </BubbleFormGroup>
    </div>
  );
}
